/**
 * The follow-up of the branch page's pods card (BranchPage.tsx): when its
 * pods — the resource tree and their placement — are read again after the
 * page's load, without a Refresh.
 *
 *  - ON A CHANGE OF THE APPLICATION: its health, sync status, deployed
 *    revision or last operation (`applicationChangeKey`), as the page learns
 *    it from a list read elsewhere — one read, bursts coalesced over
 *    `PODS_CHANGE_DEBOUNCE_MS`. Not on the first state seen: the load has
 *    just read the pods for it.
 *  - ON A RETURN TO THE TAB, when the pods were read more than
 *    `PODS_STALE_MS` ago.
 *  - EVERY `PODS_POLL_MS` WHILE SOMETHING MOVES (`isPodSettled`; for the
 *    Application, `isApplicationSettled` of `applicationSettling.ts`, which
 *    the page applies), paused while the tab is hidden
 *    (`useVisiblePolling`), stopped as soon as everything is settled. A pod
 *    that never settles (a crash loop) would poll forever: after
 *    `PODS_POLL_LIMIT` reads in a row that saw no progress
 *    (`podsProgressKey`), the poll gives up until the progress key changes —
 *    through the two triggers above — or the page loads again.
 *
 * One read at a time (`createSingleFlight`); what a read writes, and which
 * answer is stale, is the page's business.
 */

import { useEffect, useRef, useState } from 'react';
import type { PodInfo } from '../argocd/pods';
import { projectSource, type ArgoApplication } from './argoApp';
import { useOnDocumentVisible, useVisiblePolling } from './visiblePolling';

/** The poll's cadence while something moves. */
export const PODS_POLL_MS = 10_000;
/** How old the pods may be on a return to the tab before being read again. */
export const PODS_STALE_MS = 30_000;
/** The window over which changes of the Application make one read. */
export const PODS_CHANGE_DEBOUNCE_MS = 2_000;
/**
 * Polled reads in a row without progress before the poll gives up: 30 at
 * `PODS_POLL_MS`, five minutes of visible polling.
 */
export const PODS_POLL_LIMIT = 30;

/**
 * What the page knows of an Application's progress — from its own GET, or
 * from a list read since (both carry the Application whole, `argoApp.ts`).
 */
export interface ApplicationState {
  health?: string;
  sync?: string;
  /**
   * The project's commit deployed: `status.sync.revision`, or its entry of
   * `status.sync.revisions` (`projectSource`, argoApp.ts).
   */
  revision?: string;
  /** `status.operationState.phase`: `Running` while a sync is under way. */
  operationPhase?: string;
  /** `status.operationState.finishedAt`: a new one for each sync done. */
  operationFinishedAt?: string;
}

export function applicationState(app: ArgoApplication): ApplicationState {
  return {
    health: app.status?.health?.status,
    sync: app.status?.sync?.status,
    revision: projectSource(app).revision,
    operationPhase: app.status?.operationState?.phase,
    operationFinishedAt: app.status?.operationState?.finishedAt,
  };
}

/**
 * The fields whose change re-reads the pods, as one string. A sync that ends
 * changes `operationFinishedAt` even when it redeploys the same revision
 * (a manual sync, a retry).
 */
export function applicationChangeKey(state: ApplicationState): string {
  return JSON.stringify([
    state.health ?? null,
    state.sync ?? null,
    state.revision ?? null,
    state.operationPhase ?? null,
    state.operationFinishedAt ?? null,
  ]);
}

/** What a change of {@link applicationChangeKey} is made of. */
export interface ApplicationChange {
  /** Another project commit deployed ({@link ApplicationState.revision}). */
  revision: boolean;
  /** An operation begun: its phase became `Running`. */
  operationStarted: boolean;
  /** An operation ended: a new `operationState.finishedAt`. */
  operationEnded: boolean;
}

/** The fields of a change key, in the order {@link applicationChangeKey} writes them. */
function keyFields(key: string): unknown[] {
  try {
    const fields: unknown = JSON.parse(key);
    return Array.isArray(fields) ? fields : [];
  } catch {
    return [];
  }
}

/** What changed from one change key to the next. */
export function applicationChange(from: string, to: string): ApplicationChange {
  const [, , revision, phase, finishedAt] = keyFields(from);
  const [, , nextRevision, nextPhase, nextFinishedAt] = keyFields(to);
  return {
    revision: nextRevision !== revision,
    operationStarted: nextPhase === 'Running' && phase !== 'Running',
    operationEnded:
      nextFinishedAt !== null &&
      nextFinishedAt !== undefined &&
      nextFinishedAt !== finishedAt,
  };
}

/** Whether the Application is rolling something out. */
export function isApplicationMoving(state: ApplicationState): boolean {
  return state.health === 'Progressing' || state.operationPhase === 'Running';
}

/**
 * Whether a pod has reached its rest: healthy for ArgoCD — ready, not
 * pending, not crashing — and not on its way out. ArgoCD calls a pod being
 * deleted by its « Status Reason » `Terminating`, and a pending one
 * `Pending`, `ContainerCreating` or `Init:…`: any reason but `Running`
 * moves.
 */
export function isPodSettled(pod: PodInfo): boolean {
  return (
    pod.healthStatus === 'Healthy' &&
    (pod.statusReason === undefined || pod.statusReason === 'Running')
  );
}

/**
 * What progress means for the poll's give-up: the state of the Application
 * and each unsettled pod with its health. Neither restarts nor « Status
 * Reason » are part of it: both change on every turn of a crash loop, which
 * must count as no progress.
 */
export function podsProgressKey(changeKey: string, pods: PodInfo[]): string {
  const unsettled = pods
    .filter(pod => !isPodSettled(pod))
    .map(pod => `${pod.name}=${pod.healthStatus}`)
    .sort();
  return JSON.stringify([changeKey, unsettled]);
}

/**
 * `read`, one at a time. Asked while a read is in flight, it returns that
 * read — followed by one more when `queue` is set: what changed since the
 * flight started may have been missed by it. Never rejects.
 */
export function createSingleFlight(
  read: () => Promise<unknown>,
): (queue: boolean) => Promise<void> {
  let inFlight: Promise<void> | undefined;
  let again = false;
  const run = (queue: boolean): Promise<void> => {
    if (inFlight) {
      again = again || queue;
      return inFlight;
    }
    const flight = read()
      .then(
        () => undefined,
        () => undefined,
      )
      .then((): Promise<void> | undefined => {
        inFlight = undefined;
        if (again) {
          again = false;
          return run(false);
        }
        return undefined;
      });
    inFlight = flight;
    return flight;
  };
  return run;
}

export interface PodsFollowUpOptions {
  /**
   * The Application whose pods the page holds — undefined while its load
   * reads them, which stops everything. A new one (a load, Refresh) starts
   * the follow-up over.
   */
  load: object | undefined;
  /** `applicationChangeKey` of what the page knows of it. */
  changeKey: string | undefined;
  /** Whether something moves: the poll runs while it holds. */
  moving: boolean;
  /** `podsProgressKey` of the pods held. */
  progressKey: string;
  /** When the pods were last read with success (`Date.now()`). */
  lastReadAt: () => number | undefined;
  /** The quiet read of the pods; its failures are its own. */
  read: () => Promise<unknown>;
  /**
   * Told of the same debounced change the pods are read again for, with the
   * change key before the burst and after it — the page's other quiet
   * re-reads ride on it (BranchPage.tsx). Not on a return to the tab nor on
   * the poll.
   */
  onChange?: (from: string, to: string) => void;
}

export function usePodsFollowUp(options: PodsFollowUpOptions): void {
  const { load, changeKey, moving, progressKey } = options;
  const latest = useRef(options);
  latest.current = options;
  const [run] = useState(() => createSingleFlight(() => latest.current.read()));

  // The state of the poll's give-up: the reads in a row that started from
  // the same progress, and the progress it gave up on.
  const streak = useRef<{ load?: object; key?: string; count: number }>({
    count: 0,
  });
  const [gaveUp, setGaveUp] = useState<{ load: object; key: string }>();
  const polling =
    load !== undefined &&
    moving &&
    !(gaveUp?.load === load && gaveUp.key === progressKey);

  // A CHANGE OF THE APPLICATION: one read, `PODS_CHANGE_DEBOUNCE_MS` after
  // the last change of a burst (each change clears the previous timer). The
  // first state seen for a load only records it. `base`: the key the last
  // read was for — what `onChange` compares the burst's end with.
  const seen = useRef<{ load: object; key: string; base: string }>();
  useEffect(() => {
    if (load === undefined || changeKey === undefined) {
      return undefined;
    }
    const previous = seen.current;
    if (previous?.load !== load) {
      seen.current = { load, key: changeKey, base: changeKey };
      return undefined;
    }
    if (previous.key === changeKey) {
      return undefined;
    }
    const current = { load, key: changeKey, base: previous.base };
    seen.current = current;
    const timer = setTimeout(() => {
      current.base = changeKey;
      void run(true);
      latest.current.onChange?.(previous.base, changeKey);
    }, PODS_CHANGE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [load, changeKey, run]);

  // A RETURN TO THE TAB. Not while polling: the poll reads at once on that
  // same return (`useVisiblePolling`).
  useOnDocumentVisible(() => {
    const readAt = latest.current.lastReadAt();
    if (
      latest.current.load !== undefined &&
      !polling &&
      (readAt === undefined || Date.now() - readAt > PODS_STALE_MS)
    ) {
      void run(false);
    }
  });

  // WHILE SOMETHING MOVES.
  useVisiblePolling(
    () => {
      const current = latest.current;
      if (current.load === undefined) {
        return undefined;
      }
      if (
        streak.current.load !== current.load ||
        streak.current.key !== current.progressKey
      ) {
        streak.current = {
          load: current.load,
          key: current.progressKey,
          count: 0,
        };
      }
      if (streak.current.count >= PODS_POLL_LIMIT) {
        setGaveUp({ load: current.load, key: current.progressKey });
        return undefined;
      }
      streak.current.count += 1;
      return run(false);
    },
    PODS_POLL_MS,
    polling,
  );
}
